import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EditNoteIcon from "@mui/icons-material/EditNote";

// Tailwind classes for the per-side offer chip (border = side colour @25%, fill = side colour @7%)
const OFFER_CHIP_CLASS = {
  PLAINTIFF: "border-[#2f6fd640] bg-[#2f6fd612]",
  DEFENDANT: "border-[#e07b3940] bg-[#e07b3912]",
  NEUTRAL: "border-[#7a869a40] bg-[#7a869a12]",
};
// MUI theme.shadows[1], used for the offer dots
const DOT_SHADOW =
  "shadow-[0px_2px_1px_-1px_rgba(0,0,0,0.2),0px_1px_1px_0px_rgba(0,0,0,0.14),0px_1px_3px_0px_rgba(0,0,0,0.12)]";
const SIDE_COLORS = {
  PLAINTIFF: "#2f6fd6",
  DEFENDANT: "#e07b39",
  NEUTRAL: "#7a869a",
};

const LOADING_STEPS = [
  "Reading the offer history...",
  "Comparing how both sides have moved...",
  "Estimating the settlement zone...",
  "Preparing your suggestions...",
];

// Literal class names so Tailwind can see them.
const SECTION_DELAYS = {
  0: "[animation-delay:0ms]",
  80: "[animation-delay:80ms]",
  160: "[animation-delay:160ms]",
  240: "[animation-delay:240ms]",
  320: "[animation-delay:320ms]",
  400: "[animation-delay:400ms]",
};

const MOMENTUM = {
  CONVERGING: { label: "Parties are converging", color: "success", Icon: TrendingDownIcon },
  STALLED: { label: "Negotiation has stalled", color: "warning", Icon: TrendingFlatIcon },
  DIVERGING: { label: "Gap is widening", color: "error", Icon: TrendingUpIcon },
};

function toUSD(money) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(money);
}

function sideLabel(side, byViewer, perspective) {
  if (byViewer) return "You";
  if (perspective !== "NEUTRAL" && side !== "NEUTRAL" && side !== perspective) return "Other party";
  return { PLAINTIFF: "Plaintiff", DEFENDANT: "Defendant", NEUTRAL: "Admin" }[side] || side;
}

function Section({ title, icon, children, delay = 0 }) {
  return (
    <Box
      className={`mt-5 animate-fade-in ${SECTION_DELAYS[delay]}`}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        {icon}
        <Typography variant="overline" color="text.secondary" lineHeight={1.5}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

function LikelihoodGauge({ value }) {
  const colorClass = value >= 70 ? "text-[#2e9d5b]" : value >= 40 ? "text-[#e0a100]" : "text-[#d64545]";
  return (
    <Tooltip title="An AI estimate based on how the offers are moving, not a prediction">
      <Box position="relative" display="inline-flex" flexShrink={0}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={92}
          thickness={4}
          className="text-[#e6ecf3] absolute"
        />
        <CircularProgress
          variant="determinate"
          value={value}
          size={92}
          thickness={4}
          className={`${colorClass} [&_circle]:[stroke-linecap:round]`}
        />
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Typography fontWeight={700} fontSize={22} lineHeight={1}>
            {value}%
          </Typography>
          <Typography fontSize={9} color="text.secondary" mt={0.3} textAlign="center" lineHeight={1.2}>
            est. chance
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
}

// Horizontal track: each side's latest offer, the gap between them and the AI range.
function SettlementZone({ journey, recommendedOffer, perspective }) {
  const latest = journey?.stats?.latestOfferBySide || {};
  const markers = ["PLAINTIFF", "DEFENDANT"]
    .filter((side) => latest[side] !== undefined)
    .map((side) => ({
      side,
      amount: latest[side],
      label: sideLabel(side, perspective === side, perspective),
    }));
  if (markers.length < 2) return null;

  const amounts = [...markers.map((m) => m.amount)];
  if (recommendedOffer) amounts.push(recommendedOffer.min, recommendedOffer.max);
  const low = Math.min(...amounts);
  const high = Math.max(...amounts);
  const pad = (high - low) * 0.15 || 1;
  const pos = (n) => `${((n - (low - pad)) / (high - low + 2 * pad)) * 100}%`;

  const [a, b] = markers.map((m) => m.amount).sort((x, y) => x - y);

  return (
    <Box>
      <Box position="relative" height={64} mx={1}>
        <Box
          position="absolute"
          top={30}
          left={0}
          right={0}
          height={6}
          borderRadius={3}
          bgcolor="#e6ecf3"
        />
        <Box
          position="absolute"
          top={30}
          height={6}
          left={pos(a)}
          width={`calc(${pos(b)} - ${pos(a)})`}
          className="bg-[repeating-linear-gradient(45deg,#f5c6a5,#f5c6a5_4px,#fbe3d2_4px,#fbe3d2_8px)]"
        />
        {recommendedOffer ? (
          <Tooltip title={`AI suggested range ${toUSD(recommendedOffer.min)} – ${toUSD(recommendedOffer.max)}`}>
            <Box
              position="absolute"
              top={26}
              height={14}
              left={pos(recommendedOffer.min)}
              width={`max(8px, calc(${pos(recommendedOffer.max)} - ${pos(recommendedOffer.min)}))`}
              borderRadius={2}
              className="bg-[rgba(46,157,91,0.85)] shadow-[0_0_0_3px_rgba(46,157,91,0.2)]"
            />
          </Tooltip>
        ) : null}
        {markers.map((m, i) => (
          <Box
            key={m.side}
            position="absolute"
            left={pos(m.amount)}
            top={i === 0 ? 0 : 38}
            className="[transform:translateX(-50%)] text-center"
          >
            {i === 1 ? (
              <Box width={14} height={14} mx="auto" mb={0.3} borderRadius="50%" border="3px solid #fff" bgcolor={SIDE_COLORS[m.side]} className={`${DOT_SHADOW} mt-[-9.6px]`} />
            ) : null}
            <Typography fontSize={11} fontWeight={600} color={SIDE_COLORS[m.side]} whiteSpace="nowrap">
              {m.label} · {toUSD(m.amount)}
            </Typography>
            {i === 0 ? (
              <Box width={14} height={14} mx="auto" mt={0.3} borderRadius="50%" border="3px solid #fff" bgcolor={SIDE_COLORS[m.side]} className={DOT_SHADOW} />
            ) : null}
          </Box>
        ))}
      </Box>
      <Box display="flex" gap={2} mt={1} flexWrap="wrap">
        <Typography fontSize={12} color="text.secondary">
          <Box component="span" className="inline-block w-[10px] h-[10px] mr-1 bg-[#f5c6a5] rounded-[2px]" />
          Gap {toUSD(journey.stats.currentGap)}
        </Typography>
        {recommendedOffer ? (
          <Typography fontSize={12} color="text.secondary">
            <Box component="span" className="inline-block w-[10px] h-[10px] mr-1 bg-[#2e9d5b] rounded-[2px]" />
            AI suggested range
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function OfferJourney({ journey, perspective }) {
  const offers = journey?.offers || [];
  if (!offers.length) return null;

  return (
    <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
      {offers.map((o, i) => (
        <Box key={i} display="flex" alignItems="center" gap={0.5}>
          <Box className={`px-[9.6px] py-[4.8px] rounded-lg border border-solid ${OFFER_CHIP_CLASS[o.side] || ""}`}>
            <Typography fontSize={10} color="text.secondary" lineHeight={1.2}>
              R{i + 1} · {sideLabel(o.side, o.byViewer, perspective)}
            </Typography>
            <Typography fontSize={13} fontWeight={700} color={SIDE_COLORS[o.side]}>
              {toUSD(o.amount)}
            </Typography>
          </Box>
          {i < offers.length - 1 ? (
            <Typography color="text.disabled" fontSize={14}>
              →
            </Typography>
          ) : null}
        </Box>
      ))}
    </Box>
  );
}

function LoadingState() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setStep((s) => (s + 1) % LOADING_STEPS.length), 1600);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box py={3}>
      <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
        <AutoAwesomeIcon className="!text-[44px] text-[#2f6fd6] animate-pulse-icon" />
        <Typography mt={1.5} fontWeight={500} key={step} className="animate-fade-in-once">
          {LOADING_STEPS[step]}
        </Typography>
      </Box>
      <Skeleton variant="rounded" height={90} className="mb-4" />
      <Skeleton variant="rounded" height={60} className="mb-4" />
      <Skeleton width="60%" />
      <Skeleton width="80%" />
    </Box>
  );
}

export default function AiNegotiationDialog({
  open,
  onClose,
  loading,
  insight,
  onRegenerate,
  onUseAmount,
  onUseMessage,
}) {
  const [copied, setCopied] = useState(false);
  const perspective = insight?.viewerPerspective || "NEUTRAL";
  const momentum = MOMENTUM[insight?.journey?.stats?.momentum];
  const recommended = insight?.recommendedOffer;
  const suggestedAmount = recommended ? Math.round((recommended.min + recommended.max) / 2) : null;

  function copyDraft() {
    navigator.clipboard?.writeText(insight.draftMessage).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ className: "rounded-xl overflow-hidden" }}>
      <Box className="bg-[linear-gradient(120deg,#1d3a8a_0%,#2f6fd6_55%,#1fb5c9_100%)] text-white px-6 py-5 relative">
        <IconButton onClick={onClose} size="small" className="!absolute top-[10px] right-[10px] text-[rgba(255,255,255,0.85)]">
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box display="flex" alignItems="center" gap={1.2}>
          <AutoAwesomeIcon />
          <Typography variant="h6" fontWeight={600}>
            AI Negotiation Assistant
          </Typography>
        </Box>
        <Typography fontSize={13} className="opacity-[0.85] mt-1">
          {perspective === "NEUTRAL"
            ? "Neutral view of both sides"
            : `Personalised for you · ${perspective === "PLAINTIFF" ? "Plaintiff" : "Defendant"} side`}
        </Typography>
      </Box>

      <Box className="border-0 border-b border-solid border-[#e1e7f0] bg-[#f5f7fb] px-6 py-2">
        <Typography fontSize={12} color="text.secondary">
          AI-generated estimate, not legal advice. The negotiation and case details are sent to Google&apos;s Gemini to produce this.
        </Typography>
      </Box>

      <DialogContent className="pt-5">
        {loading || !insight ? (
          <LoadingState />
        ) : (
          <>
            <Box display="flex" gap={2.5} alignItems="center" className="animate-fade-in">
              {insight.settlementLikelihood !== null && insight.settlementLikelihood !== undefined ? (
                <LikelihoodGauge value={insight.settlementLikelihood} />
              ) : null}
              <Box>
                <Typography fontWeight={600} lineHeight={1.45}>
                  {insight.summary}
                </Typography>
                {momentum ? (
                  <Chip
                    size="small"
                    color={momentum.color}
                    variant="outlined"
                    icon={<momentum.Icon fontSize="small" />}
                    label={momentum.label}
                    className="mt-2"
                  />
                ) : null}
              </Box>
            </Box>

            {insight.journey?.stats?.currentGap ? (
              <Section title="Settlement zone" delay={80}>
                <SettlementZone journey={insight.journey} recommendedOffer={recommended} perspective={perspective} />
              </Section>
            ) : null}

            {recommended ? (
              <Section title="Suggested counter-offer" delay={160}>
                <Box className="p-4 rounded-lg border border-solid border-[#bfe3cc] bg-[#f1faf4] flex items-center gap-4 flex-wrap">
                  <Box flex={1} minWidth={200}>
                    <Typography fontSize={22} fontWeight={700} color="#1f7a45">
                      {toUSD(recommended.min)} – {toUSD(recommended.max)}
                    </Typography>
                    {recommended.rationale ? (
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {recommended.rationale}
                      </Typography>
                    ) : null}
                  </Box>
                  {onUseAmount ? (
                    <Button
                      variant="contained"
                      color="success"
                      className="normal-case rounded-lg"
                      onClick={() => onUseAmount(suggestedAmount)}
                    >
                      Use {toUSD(suggestedAmount)}
                    </Button>
                  ) : null}
                </Box>
              </Section>
            ) : null}

            {insight.draftMessage ? (
              <Section title="Draft message" icon={<EditNoteIcon fontSize="small" color="action" />} delay={240}>
                <Box className="p-4 rounded-lg bg-[#f5f7fb] border border-solid border-[#e1e7f0]">
                  <Typography variant="body2" fontStyle="italic" lineHeight={1.6}>
                    “{insight.draftMessage}”
                  </Typography>
                  <Box display="flex" gap={1} mt={1.5} justifyContent="flex-end">
                    <Button size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={copyDraft} className="normal-case">
                      {copied ? "Copied" : "Copy"}
                    </Button>
                    {onUseMessage ? (
                      <Button size="small" variant="outlined" onClick={() => onUseMessage(insight.draftMessage)} className="normal-case">
                        Use this message
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              </Section>
            ) : null}

            <Section title="Offer journey" delay={320}>
              <OfferJourney journey={insight.journey} perspective={perspective} />
            </Section>

            <Section title="Next step" icon={<LightbulbOutlinedIcon fontSize="small" color="warning" />} delay={400}>
              <Typography variant="body2">{insight.suggestedNextStep}</Typography>
              {insight.points?.length ? (
                <Box component="ul" className="pl-5 mt-2 mb-0">
                  {insight.points.map((point, index) => (
                    <Typography component="li" variant="body2" color="text.secondary" key={`${point}-${index}`} mt={0.4}>
                      {point}
                    </Typography>
                  ))}
                </Box>
              ) : null}
            </Section>

            <Box
              mt={3}
              pt={1.5}
              borderTop="1px solid #eef1f5"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <Typography fontSize={11} color="text.secondary">
                AI suggestions are guidance only, not legal advice. You decide what to send.
              </Typography>
              <Button size="small" startIcon={<RefreshIcon fontSize="small" />} onClick={onRegenerate} className="normal-case shrink-0">
                Regenerate
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
